import React from 'react';
import * as LucideIcons from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const FAQ = () => {
  const faqs = [
    {
      question: 'What areas do you cover?',
      answer: 'We provide construction and renovation services across Suffolk, Essex, and London, including Ipswich, Colchester, Capel St. Mary, Needham Market, and surrounding areas.'
    },
    {
      question: 'How do I get a quote?',
      answer: 'You can request a free quote by filling out our contact form, calling us at +44 0333 335 5532, or emailing info@tandmbuilders.co.uk. We\'ll arrange a site visit to discuss your requirements and provide a detailed quote.'
    },
    {
      question: 'Do you handle planning permission?',
      answer: 'Yes, we can assist with planning permission applications for extensions and other projects that require local authority approval. We have experience working with councils across our service areas.'
    },
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary depending on scope and complexity. A kitchen renovation might take 2-3 weeks, while a full house refurbishment could take 2-3 months. We provide detailed timelines during the planning phase.'
    },
    {
      question: 'Do you provide guarantees on your work?',
      answer: 'Yes, we stand behind all our work with comprehensive guarantees. Specific warranty periods depend on the type of work completed, and we\'ll discuss this with you before starting any project.'
    },
    {
      question: 'Can you work on commercial properties?',
      answer: 'Absolutely! We have extensive experience with both residential and commercial projects, including offices, retail spaces, and other commercial properties.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept bank transfers, cheques, and major credit/debit cards. Payment terms are typically structured in stages based on project milestones.'
    },
    {
      question: 'Do I need to be present during the work?',
      answer: 'While it\'s not necessary to be present at all times, we recommend being available for key milestones and decision-making points. We\'ll keep you updated throughout the project.'
    },
    {
      question: 'Are you insured?',
      answer: 'Yes, we carry full public liability insurance and employers\' liability insurance to protect both our clients and our team.'
    },
    {
      question: 'Can you help with emergency repairs?',
      answer: 'Yes, we offer emergency repair services for urgent issues. Contact us immediately on +44 0333 335 5532 and we\'ll respond as quickly as possible.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="FAQ - Construction Questions Answered | Ipswich Builders"
        description="Frequently asked questions about T&M Construction services. Learn about our quotes, timelines, payment terms, and building process. Trusted builders in Ipswich, Suffolk."
        keywords="builders FAQ, construction questions, renovation timeline, building quote process, payment terms builders, construction process explained, builders Ipswich questions"
        canonical="/faq"
      />
      <section className="pt-56 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h1>
            <p className="text-xl text-gray-600">
              Find answers to common questions about our services, process, and policies
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-2 border-gray-200 rounded-lg px-6 hover:border-orange-600 transition-colors">
                  <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:text-orange-600 py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-6 text-base leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-white rounded-2xl shadow-xl p-12">
              <LucideIcons.HelpCircle className="w-16 h-16 text-orange-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
              <p className="text-gray-600 mb-8">
                Can't find the answer you're looking for? Our friendly team is here to help.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white px-8">
                  <LucideIcons.MessageSquare className="w-5 h-5 mr-2" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;